// FSPManagementView.tsx
import React, {useEffect, useState} from 'react';
import { Table, Switch, Button, Modal, Form, Input, Select, Space } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import {FspDto} from "@/shared/api/model";
import {useGetAllFsps as useGetAllFsps,
    useCreateFsp,
    useDeleteFsp,
    useUpdateFsp,
    useUpdateFspIncomingActiveStatus,
    useUpdateFspOutgoingActiveStatus
} from "@/shared/api/client/fsp-management";
import {FspType} from "@/shared/api/model";



const { Option } = Select;

const FSPManagementView: React.FC = () => {
    const { dark } = useTheme();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingFsp, setEditingFsp] = useState<FspDto | null>(null);
    const [searchText, setSearchText] = useState('');
    const [filteredFsps, setFilteredFsps] = useState<FspDto[]>([]);

    // Queries and Mutations
    const { data: fsps, isLoading, refetch } = useGetAllFsps();
    const { mutateAsync: updateFsp } = useUpdateFsp();
    const { mutateAsync: createFsp } = useCreateFsp();
    const { mutateAsync: deleteFsp } = useDeleteFsp();
    const { mutateAsync: updateOutgoingActiveStatus } = useUpdateFspOutgoingActiveStatus();
    const { mutateAsync: updateIncomingActiveStatus } = useUpdateFspIncomingActiveStatus();

    const handleToggleIncomingActive = async (record: FspDto, active: boolean) => {
        try {
            await updateIncomingActiveStatus({
                // @ts-ignore
                code: record.code,
                params: { active }
            });
            showSuccessNotification(
                'Success',
                `FSP ${active ? 'activated' : 'deactivated'} successfully`,
                dark
            );
            refetch();
        } catch (error) {
            showErrorNotification(
                'Error',
                `Failed to ${active ? 'activate' : 'deactivate'} FSP`,
                dark
            );
        }
    };


    const handleToggleOutgoingActive = async (record: FspDto, active: boolean) => {
        try {
            await updateOutgoingActiveStatus({
                // @ts-ignore
                code: record.code,
                params: { active }
            });
            showSuccessNotification(
                'Success',
                `FSP ${active ? 'activated' : 'deactivated'} successfully`,
                dark
            );
            refetch();
        } catch (error) {
            showErrorNotification(
                'Error',
                `Failed to ${active ? 'activate' : 'deactivate'} FSP`,
                dark
            );
        }
    };

    const showModal = (record?: FspDto) => {
        setEditingFsp(record || null);
        if (record) {
            form.setFieldsValue(record);
        } else {
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingFsp) {
                await updateFsp({
                    // @ts-ignore
                    code: editingFsp.code,
                    data: values
                });
                showSuccessNotification('Success', 'FSP updated successfully', dark);
            } else {
                await createFsp({ data: values });
                showSuccessNotification('Success', 'FSP created successfully', dark);
            }
            setIsModalVisible(false);
            refetch();
        } catch (error) {
            showErrorNotification(
                'Error',
                `Failed to ${editingFsp ? 'update' : 'create'} FSP`,
                dark
            );
        }
    };

    const handleDelete = async (record: FspDto) => {
        try {
            // @ts-ignore
            await deleteFsp({ code: record.code });
            showSuccessNotification('Success', 'FSP deleted successfully', dark);
            refetch();
        } catch (error) {
            showErrorNotification('Error', 'Failed to delete FSP', dark);
        }
    };

    // useEffect to handle filtering
    useEffect(() => {
        if (!fsps) return;
        const filtered = fsps?.filter(fsp =>
            fsp.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            fsp.institutionName?.toLowerCase().includes(searchText.toLowerCase()) ||
            fsp.code?.toLowerCase().includes(searchText.toLowerCase())
        );
        // @ts-ignore
        setFilteredFsps(filtered);
    }, [fsps, searchText]);

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            sorter: (a: FspDto, b: FspDto) => (a.code || '').localeCompare(b.code || ''),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: FspDto, b: FspDto) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Type',
            dataIndex: 'fspType',
            key: 'fspType',
            sorter: (a: FspDto, b: FspDto) => (a.fspType || '').localeCompare(b.fspType || ''),

        },
        {
            title: 'Incoming Active',
            dataIndex: 'incomingActive',
            key: 'incomingActive',
            render: (_: any, record: FspDto) => (
                <Switch
                    checked={record.incomingActive}
                    onChange={(checked) => handleToggleIncomingActive(record, checked)}
                />
            ),
            sorter: (a: FspDto, b: FspDto) => ((a.incomingActive)? '1':'0').localeCompare((b.incomingActive)?'1':'0'),

        },
        {
            title: 'Outgoing Active',
            dataIndex: 'outgoingActive',
            key: 'outgoingActive',
            render: (_: any, record: FspDto) => (
                <Switch
                    checked={record.outgoingActive}
                    onChange={(checked) => handleToggleOutgoingActive(record, checked)}
                />
            ),
            sorter: (a: FspDto, b: FspDto) => ((a.outgoingActive)? '1':'0').localeCompare((b.outgoingActive)?'1':'0'),

        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: FspDto) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => showModal(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className={dark ? 'dark-mode' : ''}>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal()}
                    >
                        Add FSP
                    </Button>
                    <Input.Search
                        placeholder="Search FSPs..."
                        allowClear
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: 300 }}
                        className={dark ? 'dark-mode-input' : ''}
                    />
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={filteredFsps}
                loading={isLoading}
                rowKey="code"
            />

            <Modal
                title={editingFsp ? 'Edit FSP' : 'Add FSP'}
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="code"
                        label="Code"
                        rules={[{ required: true, message: 'Please enter FSP code' }]}
                    >
                        <Input disabled={!!editingFsp} />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please enter FSP name' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="institutionName"
                        label="Institution Name"
                        rules={[{ required: true, message: 'Please enter institution name' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="fspType"
                        label="Type"
                        rules={[{ required: true, message: 'Please select FSP type' }]}
                    >
                        <Select>
                            {Object.values(FspType).map((type) => (
                                <Option key={type} value={type}>
                                    {type}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {editingFsp && (
                        <Form.Item
                            name="incomingActive"
                            valuePropName="checked"
                            label="Incoming Active"
                        >
                            <Switch />
                        </Form.Item>
                    )}


                    {editingFsp && (
                        <Form.Item
                            name="outgoingActive"
                            valuePropName="checked"
                            label="Outgoing Active"
                        >
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default FSPManagementView;